import Big from 'big.js';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { authPaths } from '@/modules/auth/routes.tsx';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import UploadDocumentsBlock from '@/modules/common/components/UploadDocumentsBlock.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { APPLY_REGISTER_ID } from '@/modules/common/constants/search-params.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { useCreateTransferMutation } from '@/modules/investor-dashboard/queries/transfers.tsx';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import {
  Button,
  CustomCheckbox,
  ModalContentWrapper,
  Select,
  SelectOption,
  TextInputField,
} from 'ui';
import { AlertIcon } from 'ui/icons';
import { copyTextToClipBoard } from 'ui/utils';
import { TransferShareholderType } from 'api';

const validationSchema = yup.object({
  to_shareholder_id: yup.number().when('recipient_shareholder_type', {
    is: TransferShareholderType.Exist,
    then: schema => {
      return schema.moreThan(0, 'Please choose recipient').required('Please choose recipient');
    },
    otherwise: schema => {
      return schema;
    },
  }),
  quantity: yup
    .number()
    .moreThan(0, 'Quantity must be more than 0')
    .required('Quantity is required'),
  total: yup.number().moreThan(0, 'Total must be mote than 0').required('Total is required'),
  document_data: yup
    .array()
    .of(yup.string().trim())
    .min(1, 'At least 1 document is required')
    .required('Please upload document'),
  recipient_shareholder_type: yup.string().trim().default('exist'),
  recipient_email: yup
    .string()
    .trim()
    .email('Must be a valid email')
    .when('recipient_shareholder_type', {
      is: TransferShareholderType.New,
      then: schema => {
        return schema.required('This field is required');
      },
      otherwise: schema => {
        return schema;
      },
    }),
  recipient_name: yup
    .string()
    .trim()
    .when('recipient_shareholder_type', {
      is: TransferShareholderType.New,
      then: schema => {
        return schema.required('This field is required');
      },
      otherwise: schema => {
        return schema;
      },
    }),
  recipient_user_first_name: yup
    .string()
    .trim()
    .when('recipient_shareholder_type', {
      is: TransferShareholderType.New,
      then: schema => {
        return schema.required('This field is required');
      },
      otherwise: schema => {
        return schema;
      },
    }),
  recipient_user_last_name: yup
    .string()
    .trim()
    .when('recipient_shareholder_type', {
      is: TransferShareholderType.New,
      then: schema => {
        return schema.required('This field is required');
      },
      otherwise: schema => {
        return schema;
      },
    }),
});

const initialValues = {
  from_shareholder_id: 0,
  to_shareholder_id: 0,
  register_class_id: 0,
  requested_at_date: new Date().toISOString(),
  quantity: 0,
  document_data: [],
  price_per_share: 0,
  memo: '',
  total: 0,
  recipient_shareholder_type: TransferShareholderType.Exist,
  recipient_email: undefined,
  recipient_name: undefined,
  recipient_user_first_name: undefined,
  recipient_user_last_name: undefined,
};

export interface TransferModalProps {
  open: boolean;
  onClose: () => void;
  from_shareholder_id: number;
  register_class_id: number;
  register_class_name: string;
  price_per_share: number;
  register_id: number;
}

export const TransferModal = ({
  open,
  register_id,
  price_per_share,
  register_class_id,
  register_class_name,
  from_shareholder_id,
  onClose,
}: TransferModalProps) => {
  const baseURL = import.meta.env.VITE_SR_URL.replace(/\/+$/, '');
  const invitationPath = authPaths.static.invitation.replace(/^\/+/, '');
  const invitationLink = `${baseURL}/${invitationPath}?${APPLY_REGISTER_ID}=${register_id}`;

  const allowFractionalShares = useGetRegister(register_id)?.allows_fractional_shares ?? true;

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      from_shareholder_id,
      register_class_id,
      price_per_share,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const processedValues = {
        ...values,
        ...(values.recipient_shareholder_type === 'exist'
          ? {
              recipient_email: undefined,
              recipient_name: undefined,
              recipient_user_first_name: undefined,
              recipient_user_last_name: undefined,
            }
          : { to_shareholder_id: undefined }),
      };
      await createTransfer(processedValues);
      resetForm();
      onClose();
    },
    enableReinitialize: true,
  });

  const { mutateAsync: createTransfer } = useCreateTransferMutation({
    setErrors: formik.setErrors,
    registerId: register_id,
  });

  const { data: registerShareholderList, isLoading: isRegisterShareholderListLoading } =
    useRegisterShareholderList({ register_id });

  const registerShareholderListOptions = useMemo<SelectOption<number>[]>(
    () =>
      registerShareholderList?.shareholders?.map(({ id, entity: { entity_name } }) => ({
        label: entity_name,
        value: id,
      })) || [],
    [registerShareholderList],
  );

  const handleChangeQuantity = (quantity: string | number) => {
    formik.setFieldValue('quantity', quantity);
    formik.setFieldValue('total', new Big(Number(quantity)).mul(price_per_share).valueOf());
  };

  const handleChangeTotal = (total: string | number) => {
    formik.setFieldValue('total', total);

    formik.setFieldValue(
      'quantity',
      new Big(Number(total))
        .div(price_per_share)
        .round(allowFractionalShares ? 2 : 0, BigRoundMode.roundHalfUp)
        .valueOf(),
    );
  };

  const handleChangeRecipient = (recipient_id: number | null) => {
    formik.setFieldValue('to_shareholder_id', recipient_id);
  };

  const handleCheck = (checked: boolean) => {
    formik.setFieldValue(
      'recipient_shareholder_type',
      checked ? TransferShareholderType.New : TransferShareholderType.Exist,
    );
  };

  const handleChangeDocuments = useCallback(
    (filesData: string[]) => {
      formik.setFieldValue('document_data', filesData);
      formik.setFieldError('document_data', '');
    },
    [formik],
  );

  return (
    <ModalContentWrapper
      open={open}
      title={
        <p className="text-[24px] leading-[32px] font-semibold">
          Transfer <span className="text-blue-700">{register_class_name}</span>
        </p>
      }
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            <Select
              label="Recipient"
              options={registerShareholderListOptions?.filter(
                ({ value }) => value !== from_shareholder_id,
              )}
              value={formik.values.to_shareholder_id}
              isLoading={isRegisterShareholderListLoading}
              error={formik.errors.to_shareholder_id}
              onChange={handleChangeRecipient}
              disabled={formik.values.recipient_shareholder_type === TransferShareholderType.New}
            />
            <CustomCheckbox
              label="Create new shareholder"
              checked={formik.values.recipient_shareholder_type === TransferShareholderType.New}
              onChange={e => handleCheck(e.target.checked)}
            />
            {formik.values.recipient_shareholder_type === TransferShareholderType.New && (
              <>
                <div
                  style={{ backgroundColor: 'rgba(3, 105, 161, 0.08)' }}
                  className="flex gap-3 py-3 px-4 rounded mt-5 mb-5"
                >
                  <div className="flex items-start -mt-2">
                    <img src={AlertIcon} alt="info" className="w-8 h-8 mt-1" />
                  </div>
                  <div>
                    <p>
                      To add a user who already exists in our system under another register, please
                      follow these steps:
                    </p>
                    <ol style={{ paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
                      <li>
                        1. Copy the invitation link:{' '}
                        <span
                          className="cursor-pointer hover:underline"
                          title="Click to copy URL"
                          onClick={() =>
                            copyTextToClipBoard(invitationLink, {
                              successMessage: 'Invitation link copied!',
                            })
                          }
                        >
                          {invitationLink}
                        </span>
                      </li>
                      <li>
                        2. Send the copied link to the user you wish to add to the current register.
                      </li>
                    </ol>
                    <p style={{ marginTop: '0.5rem' }}>
                      When the user joins this register, you will be able to see him in the
                      recipients list.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap md:flex-nowrap justify-between items-center space-x-0 md:space-x-6">
                  <TextInputField
                    name="recipient_email"
                    label="Entity email"
                    placeholder="Entity email"
                    disabled={!formik.values.register_class_id}
                  />
                  <div className="mt-5 md:mt-0 w-full">
                    <TextInputField
                      name="recipient_name"
                      label="Entity name"
                      placeholder="Entity name"
                      disabled={!formik.values.register_class_id}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap md:flex-nowrap justify-between items-center space-x-0 md:space-x-6">
                  <TextInputField
                    name="recipient_user_first_name"
                    label="User first name"
                    placeholder="User first name"
                    disabled={!formik.values.register_class_id}
                  />
                  <div className="mt-5 md:mt-0 w-full">
                    <TextInputField
                      name="recipient_user_last_name"
                      label="User last name"
                      placeholder="User last name"
                      disabled={!formik.values.register_class_id}
                    />
                  </div>
                </div>
              </>
            )}
            <CurrencyInput
              currency=""
              label="Number of units/shares you want to send"
              name="quantity"
              value={formik.values.quantity}
              onChange={handleChangeQuantity}
              allowFractional={allowFractionalShares}
            />
            <CurrencyInput
              label="Total"
              name="total"
              value={formik.values.total}
              onChange={handleChangeTotal}
            />
            <UploadDocumentsBlock
              label="Upload document JPG, PNG or PDF. Up to 10 mb"
              error={
                Array.isArray(formik.errors.document_data)
                  ? formik.errors.document_data?.[0]
                  : formik.errors.document_data
              }
              onChange={handleChangeDocuments}
              value={formik.values.document_data}
            />
          </div>
          {formik.values.recipient_shareholder_type === TransferShareholderType.New && (
            <div
              style={{ backgroundColor: 'rgba(3, 105, 161, 0.08)' }}
              className="flex gap-3 py-3 px-4 rounded mt-5 mb-5"
            >
              <img src={AlertIcon} alt="info" />
              We will create a user account and an entity for this investor using this email, and
              they will be invited to complete the KYC/KYB process.
            </div>
          )}
          <Button onClick={formik.submitForm}>Submit</Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
